import { useToast } from '@/hook/useToast';
import {
  HrBorderRadiusInputs,
  HrDarkGrey,
  HrFlexContainer,
  HrFont,
  HrFontBold,
  HrFontRegular,
  HrlighterGrey,
  HrlightestGrey,
  HrParagraph,
  HrPastelCyan,
  HrPastelGreen,
  HrPastelRed,
  HrPrimary,
  HrRed,
  HrSecondary,
  HrWhite
} from '@components/styles/hr-styles';
import { useMutation } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';
import React, { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { iconCv, iconPencil, MiniLogoBumeran2, MiniLogoZonajobs2 } from '../../utils/image-svg';
import { parseCV } from '../services/api';
import { DisplaySettingsContext } from '../store/display-settings';
import useStore from '../store/useStore';

const Flex = styled(HrFlexContainer)`
  flex-wrap: nowrap;
  justify-content: center;

  @media (max-width: 600px) {
    flex-wrap: wrap;
  }
`;

const BaseButton = styled.div`
  font-family: ${HrFont};
  font-size: ${({ fontSizeSetting }) => `${14 + (fontSizeSetting || 0)}px !important`};
  color: ${({ darkMode }) => (darkMode ? HrWhite : HrSecondary)};
  cursor: pointer;
  flex-grow: 1;
  font-weight: ${HrFontBold};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-direction: column;
  background-color: transparent;
  border: 1px solid ${HrlighterGrey};
  border-radius: ${HrBorderRadiusInputs};
  padding: 24px;
  width: 100%;
  min-height: 100%;
  text-align: center;
  position: relative;
  box-sizing: border-box;
  text-wrap: balance;

  & div.icon {
    background-color: ${({ iconBg }) => iconBg || 'transparent'};
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
  }

  & p {
    color: ${HrDarkGrey};
    margin: 0;
    font-weight: ${HrFontRegular};
    font-size: ${({ fontSizeSetting }) => `${12 + (fontSizeSetting || 0)}px !important`};
  }

  .file-drop-zone {
    font-weight: ${HrFontBold};
    font-size: ${({ fontSizeSetting }) => `${12 + (fontSizeSetting || 0)}px`};
    color: ${HrPrimary};
    width: 100%;
    padding: 24px 16px;
    background-color: ${({ darkMode }) => (darkMode ? 'transparent' : HrlightestGrey)};
    border: 1px dashed ${HrlighterGrey};
    border-radius: ${HrBorderRadiusInputs};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    box-sizing: border-box;
    gap: 24px;

    & span {
      font-size: 48px;
      color: ${({ darkMode }) => (darkMode ? HrWhite : HrSecondary)};
    }
  }

  .file-uploaded {
    box-sizing: border-box;
    color: ${({ darkMode }) => (darkMode ? HrWhite : HrSecondary)};
    font-weight: ${HrFontBold};
    font-size: ${({ fontSizeSetting }) => `${12 + (fontSizeSetting || 0)}px`};
    width: 100%;
    padding: 16px;
    background-color: ${({ darkMode }) => (darkMode ? 'transparent' : HrlightestGrey)};
    border: 1px solid ${HrlighterGrey};
    border-radius: ${HrBorderRadiusInputs};
    display: flex;
    align-items: center;
    justify-content: space-between;

    & button {
      font-size: 24px;
      color: ${({ darkMode }) => (darkMode ? HrWhite : HrSecondary)};
      cursor: pointer;
      transition: 0.3s ease;
      padding: 8px;
      border-radius: ${HrBorderRadiusInputs};
      border: none;
      background-color: transparent;

      &:hover {
        background-color: ${HrPastelGreen};
        color: ${HrPrimary};
      }

      &.hr-Trash:hover {
        background-color: ${HrPastelRed};
        color: ${HrRed};
      }
    }

    @media (max-width: 600px) {
      flex-wrap: wrap;
      justify-content: center;
      gap: 16px;
      padding-top: 24px;
    }
  }
`;

const StyledProgress = styled.progress`
  width: 100%;
  height: 8px;
  appearance: none;
  -webkit-appearance: none;
  border: none;
  background-color: ${HrWhite};
  border-radius: 4px;
  overflow: hidden;

  &::-webkit-progress-bar {
    background-color: ${HrWhite};
  }

  &::-webkit-progress-value {
    background-color: ${HrPrimary};
    border-radius: 4px;
  }

  &::-moz-progress-bar {
    background-color: ${HrPrimary};
  }
`;

const renderButton = ({ onClick, icon, label, description, bg, fontSizeSetting, darkMode }) => (
  <BaseButton type='button' fontSizeSetting={fontSizeSetting} iconBg={bg} onClick={onClick} darkMode={darkMode}>
    <div className='icon'>{icon}</div>
    {label}
    <p>{description}</p>
  </BaseButton>
);

export const StepImportCv = observer(
  ({
    vacancy,
    showModalLogin,
    setShowModalLogin,
    show,
    onCVParsed,
    cloudflareRecaptcha,
    executeRecaptcha,
    getValues,
    setValue,
    setFileSelected,
    progress,
    start,
    resetProgressBar,
    resetWidgets
  }) => {
    const inputRef = useRef();
    const [t] = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { showToast } = useToast();
    const { getDataPostulant, setFilesLoaded } = useStore();
    const [fileName, setFileName] = useState('');
    const urlBumeran = getDataPostulant.postulationOptions?.bumeran.url;
    const urlZonajobs = getDataPostulant.postulationOptions?.zonaJobs.url;
    const showBumeranButton = getDataPostulant.postulationOptions?.bumeran.allowPostulation;
    const showZonajobsButton = getDataPostulant.postulationOptions?.zonaJobs.allowPostulation;
    const nameBumeranButton = getDataPostulant.postulationOptions?.bumeran.name || t('general.hiringroom.bumeran');
    const nameZonajobsButton = getDataPostulant.postulationOptions?.zonaJobs.name || t('general.hiringroom.zonajobs');
    const { darkMode, fontSizeSetting } = useContext(DisplaySettingsContext);

    const handleRemoveFile = () => {
      setFileName('');
      if (inputRef.current) inputRef.current.value = '';
    };

    const parseCVMutation = useMutation({
      mutationFn: async (file) => {
        let token = null;
        let type = 'google';

        if (cloudflareRecaptcha) {
          const maxAttempts = 3;
          const waitTime = 3000;

          const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

          const tryGetToken = async (attempt = 1) => {
            token = getValues('cloudflare_recaptcha_token');
            if (token) return token;

            if (attempt < maxAttempts) {
              await wait(waitTime);
              return tryGetToken(attempt + 1);
            }

            return null;
          };

          token = await tryGetToken();
          type = 'cloudflare';

          if (!token) {
            throw new Error('Recaptcha no completado después de múltiples intentos.');
          }
        } else if (executeRecaptcha) {
          try {
            token = await executeRecaptcha('cv_parser');
          } catch {
            throw new Error('Error generando token reCAPTCHA.');
          }
        }

        return parseCV(file, token, type);
      },
      onSuccess: (parsedCV) => {
        if (parsedCV.statusCode !== 200) {
          resetProgressBar();
          handleRemoveFile();
          resetWidgets();
          return showToast({
            color: HrRed,
            iconClassName: 'hr-Cross',
            message: parsedCV.response.message || 'Error al procesar el CV'
          });
        }
        showToast({
          iconClassName: 'hr-Check',
          message: 'CV procesado correctamente'
        });
        return onCVParsed(parsedCV.response);
      },
      onError: (error) => {
        const message = error?.message || 'Error al procesar el CV';
        resetProgressBar();
        handleRemoveFile();
        resetWidgets();
        showToast({
          color: HrRed,
          iconClassName: 'hr-Cross',
          message
        });
      },
      onSettled: (parsedCV, error, file) => {
        const isRequestSuccess = parsedCV?.statusCode === 200;
        if (isRequestSuccess) {
          const { cv_struct_id: fileIdParsed } = parsedCV.response;
          setFilesLoaded({
            filename: file.name,
            uploadedBy: 'cv_parser',
            fileType: 'resume',
            fileIdParsed
          });
        }
      }
    });

    const handleFile = (file) => {
      if (!file) return;

      if (file.type !== 'application/pdf') {
        showToast({
          color: HrRed,
          iconClassName: 'hr-Cross',
          message: 'El archivo debe ser en formato .pdf (no escaneado o foto).'
        });
        return;
      }

      if (file.size > 15 * 1024 * 1024) {
        showToast({
          color: HrRed,
          iconClassName: 'hr-Cross',
          message: 'El archivo no debe superar los 15MB.'
        });
        return;
      }

      try {
        setFileName(file.name);
        setFileSelected({
          name: file.name,
          file,
          source: 'cv_parser'
        });
        resetProgressBar();
        start();
        parseCVMutation.mutate(file);
      } catch (e) {
        handleRemoveFile();
        showToast({
          color: HrRed,
          iconClassName: 'hr-Cross',
          message: 'Error al procesar el CV. Inténtalo de nuevo.'
        });
      }
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const file = e.dataTransfer.files?.[0];
      handleFile(file);
    };

    const triggerFileInput = () => {
      inputRef.current?.click();
    };

    const openModalBumeran = () => {
      setShowModalLogin({
        ...showModalLogin,
        show: getDataPostulant.bumeran === null,
        portal: 'bumeran',
        url: urlBumeran,
        autoLogin: false,
        apply_with: getDataPostulant.postulationOptions.bumeran.applyWith
      });
    };

    const openModalZonajobs = () => {
      setShowModalLogin({
        ...showModalLogin,
        show: getDataPostulant.zonajobs === null,
        portal: 'zonajobs',
        url: urlZonajobs,
        autoLogin: false,
        apply_with: getDataPostulant.postulationOptions.zonaJobs.applyWith
      });
    };

    const manualPostulation = () => {
      navigate({
        pathname: location.pathname,
        replace: true,
        search: location.search,
        state: { currentStep: 2 },
        hash: `#step${2}`
      });
    };

    const parserPostulation = (e) => {
      if (parseCVMutation.isLoading) return;
      const { target } = e;
      if (target.closest('.hr-Change') || target.closest('.hr-Trash')) {
        return;
      }
      triggerFileInput();
    };

    return (
      <HrFlexContainer flexDirection='column' padding='24px 16px 0 16px' style={{ display: show ? '' : 'none' }}>
        <input
          ref={inputRef}
          type='file'
          accept='.pdf'
          style={{ display: 'none' }}
          onChange={(e) => {
            handleFile(e.target.files?.[0]);
            e.target.value = '';
          }}
        />

        <HrParagraph
          fontSizeSetting={fontSizeSetting}
          textAlign='center'
          fontWeight={HrFontBold}
          color={darkMode ? 'white' : ''}
        >
          Puedes importar tu CV desde:
        </HrParagraph>

        {vacancy.enabledCvParser && (
          <BaseButton
            type='button'
            fontSizeSetting={fontSizeSetting}
            iconBg={HrPastelGreen}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            disabled={parseCVMutation.isLoading}
            onClick={parserPostulation}
            darkMode={darkMode}
          >
            <div className='icon'>{iconCv}</div>
            Carga tu cv con IA
            <p>
              Tu perfil se completará automáticamente con inteligencia artificial utilizando el archivo que subas.
              Podrás revisar la información cargada y modificarla si lo necesitas.
            </p>
            {!fileName && (
              <div className='file-drop-zone'>
                <span className='hr-Upload' />
                Haz clic aquí o arrastra y suelta tu archivo para cargarlo
              </div>
            )}
            {fileName && (
              <HrFlexContainer flexDirection='column' className='file-uploaded'>
                <HrFlexContainer justifyContent='space-between' alignItems='center' width='100%'>
                  <div className='file-name'>{fileName}</div>
                  <HrFlexContainer gap='4px' width='auto'>
                    <button
                      type='button'
                      className='hr-Change'
                      onClick={triggerFileInput}
                      aria-label='Cambiar archivo'
                      disabled={parseCVMutation.isLoading}
                      style={
                        parseCVMutation.isLoading ? { cursor: 'not-allowed', opacity: 0.6, pointerEvents: 'none' } : {}
                      }
                    />
                    <button
                      type='button'
                      className='hr-Trash'
                      onClick={handleRemoveFile}
                      aria-label='Eliminar archivo'
                      disabled={parseCVMutation.isLoading}
                      style={
                        parseCVMutation.isLoading ? { cursor: 'not-allowed', opacity: 0.6, pointerEvents: 'none' } : {}
                      }
                    />
                  </HrFlexContainer>
                </HrFlexContainer>
                {progress > 0 && <StyledProgress value={progress} max='100' />}
              </HrFlexContainer>
            )}
            <p>
              El archivo debe ser en formato <strong>.pdf (no escaneado o foto)</strong> , y no superar los 15MB. La
              conversión puede tardar unos minutos.
            </p>
          </BaseButton>
        )}

        <Flex>
          {showBumeranButton &&
            renderButton({
              onClick: openModalBumeran,
              icon: MiniLogoBumeran2,
              label: nameBumeranButton,
              description: 'Importa tu CV desde tu cuenta de Bumeran',
              bg: 'rgba(0, 13, 241, 0.1)',
              fontSizeSetting,
              darkMode
            })}

          {showZonajobsButton &&
            renderButton({
              onClick: openModalZonajobs,
              icon: MiniLogoZonajobs2,
              label: nameZonajobsButton,
              description: 'Importa tu CV desde tu cuenta de Zona Jobs',
              bg: 'rgba(254, 99, 63, 0.1)',
              fontSizeSetting,
              darkMode
            })}

          {renderButton({
            onClick: manualPostulation,
            icon: iconPencil,
            label: 'Manualmente',
            description: t('postulant.step_one.manually'),
            bg: HrPastelCyan,
            fontSizeSetting,
            darkMode
          })}
        </Flex>
      </HrFlexContainer>
    );
  }
);
